import { getApparence } from '@/api/apparence'
import { APP_NAMESPACE } from '@/meta'
import { DEFAUT_DENSITE } from './densite'
import { densiteAppliquee } from './densiteAppliquee'
import { DEFAUT_STYLE_ENTETE } from './entete'
import { enteteAppliquee } from './enteteAppliquee'
import { chargerPolices } from './polices'
import { DEFAUT_STYLE_SECTIONS } from './sections'
import { sectionsAppliquees } from './sectionsAppliquees'
import {
  REGLAGES_VIDES,
  feuilleComplete,
  normaliserReglages,
  type ReglagesApparence,
} from './reglages'

/**
 * Application de l'apparence de la plateforme au document.
 *
 * Deux temps, comme le thème (`stores/theme.ts`) :
 *
 *   1. AVANT le montage, la dernière apparence connue, lue dans le stockage
 *      local — sans elle, chaque chargement afficherait d'abord l'apparence par
 *      défaut puis basculerait sur celle de la plateforme sous les yeux de
 *      l'utilisateur ;
 *   2. en tâche de fond, la version du serveur (route PUBLIQUE : l'écran de
 *      connexion la reçoit aussi), qui remplace et met le cache à jour.
 *
 * Toute valeur passe par `normaliserReglages` avant d'entrer dans la feuille :
 * le stockage local est modifiable par n'importe quel script de la page, et une
 * valeur libre y serait une injection CSS.
 */

/**
 * Versionnée à chaque changement de forme (`.v2` : arrondis, `.v3` : polices,
 * `.v4` : boutons, `.v5` : densité, `.v6` : ombres, `.v7` : la famille `header`
 * devient une disposition — un cache `.v6` en 'marque' serait une couleur sans
 * effet ; `.v8` : en-têtes de section).
 * Une clé neuve plutôt qu'une lecture des anciennes formes — le cache n'est
 * qu'un raccourci, le serveur le remplace au premier chargement.
 */
const CLE_STOCKAGE = `${APP_NAMESPACE}.apparence.v8`
const ID_FEUILLE = 'apparence-plateforme'

/**
 * Remplace la feuille des surcharges (ou la retire s'il n'y en a aucune), et
 * télécharge les polices choisies.
 *
 * Les polices en tâche de fond, sans attendre : la feuille déclare déjà la pile
 * de repli, le texte reste lisible pendant le téléchargement. Un échec est
 * signalé en console, pas avalé — le texte reste alors dans la police du
 * système, ce qui se voit mais ne casse rien.
 */
export function appliquerApparence(reglages: ReglagesApparence): void {
  chargerPolices(reglages.fonts).catch((erreur: unknown) => {
    console.error('Police de la plateforme non chargée, police du système conservée.', erreur)
  })
  const css = feuilleComplete(reglages)
  // Après `feuilleComplete`, qui lève sur un niveau inconnu : la hauteur des
  // lignes et les espacements de la feuille changent ENSEMBLE, ou pas du tout.
  densiteAppliquee.value = reglages.density.level ?? DEFAUT_DENSITE
  // La disposition de la coquille est lue par `AppLayout.vue`, pas par le CSS.
  enteteAppliquee.value = reglages.header.style ?? DEFAUT_STYLE_ENTETE
  // La teinte des en-têtes de section dépend de la vue : lue par la fiche.
  sectionsAppliquees.value = reglages.sections.style ?? DEFAUT_STYLE_SECTIONS
  let feuille = document.getElementById(ID_FEUILLE)
  if (css === '') {
    feuille?.remove()
    return
  }
  if (feuille === null) {
    feuille = document.createElement('style')
    feuille.id = ID_FEUILLE
    document.head.appendChild(feuille)
  }
  // `textContent`, jamais `innerHTML` : la feuille n'est pas du HTML.
  feuille.textContent = css
}

function lireCache(): ReglagesApparence | null {
  try {
    const brut = localStorage.getItem(CLE_STOCKAGE)
    return brut === null ? null : normaliserReglages(JSON.parse(brut))
  } catch {
    // Stockage indisponible ou contenu illisible : l'apparence par défaut,
    // jusqu'à la réponse du serveur.
    return null
  }
}

function ecrireCache(reglages: ReglagesApparence): void {
  try {
    localStorage.setItem(CLE_STOCKAGE, JSON.stringify(reglages))
  } catch {
    // Appliquée pour cette session, simplement non mémorisée.
  }
}

/** Applique ET mémorise : après un enregistrement, ou une réponse du serveur. */
export function adopterApparence(reglages: ReglagesApparence): void {
  appliquerApparence(reglages)
  ecrireCache(reglages)
}

/** Avant le montage de Vue. Appelée depuis `main.ts`. */
export function initApparenceEager(): void {
  const enCache = lireCache()
  if (enCache !== null) appliquerApparence(enCache)

  // Un autre onglet a enregistré une nouvelle apparence : elle s'applique ici
  // aussi, sinon deux onglets de la même application divergent jusqu'au
  // prochain rechargement.
  window.addEventListener('storage', (evenement) => {
    if (evenement.key !== CLE_STOCKAGE) return
    appliquerApparence(lireCache() ?? REGLAGES_VIDES)
  })
}

/**
 * Relit l'apparence du serveur. Ne lève pas : un échec réseau laisse en place
 * la dernière apparence connue — l'application reste utilisable avec son
 * apparence habituelle. L'échec est signalé en console, pas avalé.
 *
 * Une réponse HORS CONTRAT, elle, est une erreur bruyante : la base la refuse
 * déjà, donc l'atteindre veut dire qu'un des deux côtés a changé de forme.
 */
export async function rafraichirApparence(): Promise<void> {
  let reponse: Awaited<ReturnType<typeof getApparence>>
  try {
    reponse = await getApparence()
  } catch (erreur) {
    console.error('Apparence de la plateforme indisponible, apparence conservée.', erreur)
    return
  }
  const reglages = normaliserReglages(reponse)
  if (reglages === null) {
    console.error('Apparence de la plateforme hors contrat, ignorée.', reponse)
    return
  }
  adopterApparence(reglages)
}
